import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import InsightsPreview from '@/assets/images/dashboard-preview-insights.png';
import MetricsPreview from '@/assets/images/dashboard-preview-metrics.png';
import OpeningsPreview from '@/assets/images/dashboard-preview-openings.png';
import { LockClosedIcon } from '@/components/icons/lock-closed';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/utils/classnames';

const bold = { b: (chunks: ReactNode) => <b>{chunks}</b> };

/**
 * Stand-ins for the three dashboard sections a Source subscription unlocks.
 * These are exported images of the real sections rather than the live
 * components: a free-tier account has no sourcing history to chart, so the live
 * dashboard would query for numbers nobody can read and render them empty.
 *
 * Heights match the locked overlays in the Freemium Source dashboard frame.
 * The export stretches to fill that box so the full image stays in view.
 */
const PREVIEWS = [
  {
    id: 'metrics',
    image: MetricsPreview,
    heightClass: 'h-[156px]',
    caption: (
      <FormattedMessage
        defaultMessage="<b>Overview metrics:</b> total hires, applications sourced and spend across all openings, based on a date range."
        description="Names what the first locked section of the sourcing dashboard reports on. Shown over a preview image of that section"
        values={bold}
      />
    ),
  },
  {
    id: 'insights',
    image: InsightsPreview,
    heightClass: 'h-[215px]',
    caption: (
      <FormattedMessage
        defaultMessage="<b>Cue insights:</b> what to pause, boost or post, based on sourcing performance."
        description="Names what the second locked section of the sourcing dashboard suggests. Shown over a preview image of that section. Cue is Fountain's AI assistant"
        values={bold}
      />
    ),
  },
  {
    id: 'openings',
    image: OpeningsPreview,
    heightClass: 'h-[394px]',
    caption: (
      <FormattedMessage
        defaultMessage="<b>Opening-level performance:</b> sourcing status, spend, applicants, hires, CPA, and cost per hire for each role."
        description="Names what the third locked section of the sourcing dashboard reports per job opening. Shown over a preview image of that section. CPA means cost per applicant"
        values={bold}
      />
    ),
  },
];

/**
 * Shows a free-tier account the shape of the sourcing dashboard they don't have
 * yet: each section as a faded preview, labelled with what it would tell them.
 */
export function LockedDashboard() {
  return (
    <div className="flex flex-col gap-4">
      {PREVIEWS.map(({ id, image, heightClass, caption }) => (
        <figure
          key={id}
          className={cn(
            'border-fx-neutral-200 relative w-full overflow-hidden rounded-lg border',
            heightClass,
          )}
        >
          <img
            src={image}
            alt=""
            className="size-full object-fill select-none"
          />

          <figcaption className="absolute inset-0 flex items-center justify-center bg-white/50 p-2 backdrop-blur-[3px] md:p-4">
            <Typography
              variant="medium1"
              className="shadow-dropdown border-fx-neutral-200 !text-fx-neutral-600 max-md:!text-body flex items-center gap-2 rounded-full border bg-white px-3 py-2 text-center"
            >
              <LockClosedIcon className="size-4 shrink-0" />
              {/* One flex item, or the bold lead-in and the rest of the
                  sentence get laid out as separate items. */}
              <span>{caption}</span>
            </Typography>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
